import { useQuery } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Button, type DialogProps } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { FolderContextProvider, useAppContext } from '~/contexts';
import type { ContentKind } from '~/generated/graphql';
import { ContentGrid, DialogRoot } from '../../inputs/MediaSelect/components';
import { AddContentDialogDocument } from './dialog.generated';

/* GraphQL */ `#graphql
query AddContentDialog($networkId: Int!) {
  network(id: $networkId) {
    contentFolderTree
    contentFolders {
      canUpdate {
        ...AuthorizationResult
      }
      id
      name
    }
    id
  }
}
`;

export interface AddContentSelection {
  contentItemIds: number[];
  mediaItemIds: number[];
}

export interface AddContentDialogProps extends Omit<DialogProps, 'onChange'> {
  introText?: ReactNode;
  kind?: ContentKind[];
  loading?: boolean;
  onClose: () => void;
  onChange: (selection: AddContentSelection) => void;
  submitLabel?: (count: number) => string;
  title?: string;
  value?: string[];
}

const parseSelectionId = (id: string): { type: 'ContentItem' | 'MediaItem'; id: number } | null => {
  const [type, rawId] = id.split('-');
  if (type !== 'ContentItem' && type !== 'MediaItem') return null;
  const parsed = Number(rawId);
  return Number.isNaN(parsed) ? null : { type, id: parsed };
};

export const splitSelectionIds = (selectedIds: string[]): AddContentSelection => {
  const contentItemIds: number[] = [];
  const mediaItemIds: number[] = [];
  for (const raw of selectedIds) {
    const parsed = parseSelectionId(raw);
    if (parsed?.type === 'ContentItem') contentItemIds.push(parsed.id);
    else if (parsed?.type === 'MediaItem') mediaItemIds.push(parsed.id);
  }
  return { contentItemIds, mediaItemIds };
};

export const AddContentDialog = ({
  introText,
  kind,
  loading,
  onClose,
  onChange,
  submitLabel,
  title,
  value,
  ...props
}: AddContentDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [selectedIds, setSelectedIds] = useState<string[]>(value ?? []);

  const { data } = useQuery(AddContentDialogDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const handleItemClick = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearState = () => {
    setSelectedIds([]);
  };

  const handleSubmit = () => {
    onChange(splitSelectionIds(selectedIds));
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  if (data?.network == null) return null;

  const displayTitle = title ?? t('components.playlist_detail_page.add_content_dialog_title');

  return (
    <FolderContextProvider
      folderTree={data.network.contentFolderTree}
      folders={data.network.contentFolders}
    >
      <Dialog
        PaperComponent={DialogRoot}
        TransitionProps={{ onExited: clearState }}
        fullWidth
        maxWidth="lg"
        onClose={handleClose}
        {...props}
      >
        <DialogTitle onClose={handleClose}>{displayTitle}</DialogTitle>

        <DialogContent sx={{ overflow: 'hidden' }}>
          {introText && <DialogContentTitle>{introText}</DialogContentTitle>}
          <ContentGrid
            kind={kind}
            onClick={handleItemClick}
            rememberLastFolder
            selectedIds={selectedIds}
          />
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={loading}>
            {t('common.dialogs.cancel')}
          </Button>
          <LoadingButton
            color="primary"
            variant="contained"
            disabled={selectedIds.length === 0}
            loading={loading}
            onClick={handleSubmit}
          >
            {submitLabel?.(selectedIds.length) ??
              t('common.dialogs.add_items_plural', {
                count: selectedIds.length,
              })}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FolderContextProvider>
  );
};
